import { BAPI_COMMANDS } from "../../types/commands";
import type { Result } from "../../types";
import { fetchHandler } from "@/bapi-client/fetch";
import { uploadS3File } from "..";
import type { BAPIUserLogin, HandlerParamsUploadProfilePicture } from "@/bapi-client/types/users";
import type { UserMe } from "@/bapi-client/types/user";

export async function getMe(): Promise<Result<UserMe>> {
  const response = await fetchHandler(BAPI_COMMANDS.USER_GET_ME, "GET", "/user/me");
  if (!response.success) return response;
  const parsed = await response.data.json();
  if (parsed.success) {
    parsed.data.companies.sort((a, b) => a.name.localeCompare(b.name));
  }
  return parsed;
}

export async function loginUser(credentials: BAPIUserLogin): Promise<Result<UserMe>> {
  const response = await fetchHandler(BAPI_COMMANDS.USER_LOGIN, "POST", "/user/login", {
    body: credentials,
  });
  if (!response.success) return response;
  const parsed = await response.data.json();
  return parsed;
}

export async function logoutUser(): Promise<Result<boolean>> {
  await fetchHandler(BAPI_COMMANDS.USER_LOGOUT, "DELETE", "/user/me");
  return { success: true, data: true };
}

export async function oauthLogoutUser(): Promise<Result<boolean>> {
  await fetchHandler(BAPI_COMMANDS.USER_OAUTH_LOGOUT, "DELETE", "/user/oauth/logout");
  return { success: true, data: true };
}

export async function getProfilePictureUpload() {
  return await fetchHandler(BAPI_COMMANDS.USER_GET_PROFILE_PICTURE_UPLOAD_URL, "GET", "/user/profile_picture");
}

export async function uploadUserProfilePicture(
  ...[file, altText]: HandlerParamsUploadProfilePicture
): Promise<Result<boolean>> {
  const signedResult = await getProfilePictureUpload();
  if (!signedResult.success) return signedResult;
  const parsedUpload = await signedResult.data.json();
  if (!parsedUpload.success) return parsedUpload;
  const { upload_url, attachment_id } = parsedUpload.data;

  const s3Result = await uploadS3File(file, upload_url);
  if (!s3Result.success) return s3Result;

  const uploadResult = await fetchHandler(BAPI_COMMANDS.USER_UPLOAD_PROFILE_PICTURE, "PUT", "/user/profile_picture", {
    body: {
      alt_text: altText,
      attachment_id,
      filename: file.name,
      filesize: file.size,
      mime_type: file.type,
    },
  });

  if (!uploadResult.success) return uploadResult;
  return { success: true, data: true };
}

export async function getPasswordResetEmail(email: string): Promise<Result<boolean>> {
  const response = await fetchHandler(BAPI_COMMANDS.USER_GET_PASSWORD_RESET_EMAIL, "POST", "/user/forgot", {
    body: { email },
  });
  if (!response.success) return response;
  return { success: true, data: true };
}

export async function resetUserPassword(password: string, token: string): Promise<Result<boolean>> {
  const response = await fetchHandler(BAPI_COMMANDS.USER_RESET_PASSWORD, "POST", "/user/reset", {
    body: {
      password,
      token,
    },
  });
  if (!response.success) return response;
  return { success: true, data: true };
}

export async function getUserPermissions(customerId: string): Promise<Result<string[]>> {
  const response = await fetchHandler(BAPI_COMMANDS.USER_PERMISSIONS, "GET", `/permissions/${customerId}`);
  if (!response.success) return response;

  const parsed = await response.data.json();
  if (!parsed.success) return parsed;

  return {
    success: true,
    data: parsed.data.permissions,
  };
}

export async function addUserCustomerAssociation(
  user_id: string,
  { customer_id, role_id }: { customer_id: string; role_id: string },
) {
  const response = await fetchHandler(
    BAPI_COMMANDS.ADMIN_USER_ADD_CUSTOMER,
    "PUT",
    `/admin/users/${user_id}/customers/${customer_id}`,
    {
      body: { role_id },
    },
  );
  if (!response.success) return response;
  return await response.data.json();
}

export async function deleteUserCustomerAssociation(user_id: string, customer_id: string) {
  const response = await fetchHandler(
    BAPI_COMMANDS.ADMIN_USER_ADD_CUSTOMER,
    "DELETE",
    `/admin/users/${user_id}/customers/${customer_id}`,
  );
  if (!response.success) return response;
  return await response.data.json();
}
